import { useViewportSize } from '@mantine/hooks';
import { ThankYouShareProps } from '../../types/PropsTypes';
import { useContext, useEffect, useState } from 'react';
import { CompanyContext } from '../../contexts/CompanyContext';
import {
  IconBrandInstagram,
  IconMail,
  IconMessage2,
  IconShare,
} from '../icons/TablerIcons';
import { facebookIconPrimary, tiktokIcon } from '../icons/BrandIcons';
import { Title } from '@mantine/core';
import { safeOpen } from '../../services/UtilService';
import { useOfferImage } from '../../hooks/useOfferImage';

async function createShareImageFile(
  rewardsOfferImg: string,
  signal?: AbortSignal,
): Promise<File | null> {
  const markdownUrl = rewardsOfferImg.match(/\((https?:\/\/[^)]+)\)$/)?.[1];
  const imageUrl = markdownUrl ?? rewardsOfferImg;
  const imageRequestUrl = new URL(imageUrl);
  imageRequestUrl.searchParams.set('share-cache-bust', Date.now().toString());
  const response = await fetch(imageRequestUrl, {
    mode: 'cors',
    signal,
  });

  if (!response.ok) {
    return null;
  }

  const imageBlob = await response.blob();
  if (!imageBlob.type.startsWith('image/')) {
    return null;
  }

  const extension = imageBlob.type === 'image/jpeg'
    ? 'jpg'
    : imageBlob.type.split('/')[1] || 'png';

  return new File(
    [imageBlob],
    `rewards-offer.${extension}`,
    { type: imageBlob.type },
  );
}

export function ThankYouShare({
  url,
  name,
  isOffer,
  rewardsOfferImg,
  contactUid,
}: ThankYouShareProps): JSX.Element {
  const { width } = useViewportSize();
  const { company } = useContext(CompanyContext);
  const [shareImageFile, setShareImageFile] = useState<File | null>(null);
  const [isShareImageLoading, setIsShareImageLoading] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const { offerImg, isGenerating: isGeneratingOfferImg } = useOfferImage(
    contactUid,
    rewardsOfferImg,
  );
  const isPreparingShareImage = isGeneratingOfferImg || isShareImageLoading;

  const companyName = company?.name ?? 'this company';
  const referralRewardsEnabled = isOffer && Boolean(company?.referralRewardsEnabled);
  const referralOfferEnabled = referralRewardsEnabled
    && Boolean(company?.referralOfferEnabled);
  const showReward = referralRewardsEnabled && Boolean(company?.promoterReward);

  useEffect(() => {
    setShareImageFile(null);

    if (!offerImg) {
      setIsShareImageLoading(false);
      return;
    }

    const abortController = new AbortController();
    setIsShareImageLoading(true);

    const preloadShareImage = async () => {
      try {
        const imageFile = await createShareImageFile(
          offerImg,
          abortController.signal,
        );
        setShareImageFile(imageFile);
      } catch {
        // The URL-only share remains available if the CDN image cannot load.
      } finally {
        if (!abortController.signal.aborted) {
          setIsShareImageLoading(false);
        }
      }
    };

    void preloadShareImage();

    return () => abortController.abort();
  }, [offerImg]);

  const onClickEmail = () => {
    const subject = 'Check Out ' + (isOffer ? 'This Offer' : 'My Review');
    const offerBody = `Hi, ${name}. I wanted to let you know that ${company?.name} did an amazing job and they're offering ${company?.offerTitle}! Click here: \n${url}`;
    const reviewBody = `Hey,\nI recently left a review for ${company?.name}, you should check it out!\nHere is the link: ${url}`;
    safeOpen(
      encodeURI(`mailto:?subject=${subject}&body=${isOffer ? offerBody : reviewBody}`),
      'height=500,width=530,menubar=no,status=no,toolbar=no'
    );
  };

  const onClickMobile = () => {
    const offerBody = `Hi, ${name}. I wanted to let you know that ${company?.name} did an amazing job and they're offering ${company?.offerTitle}! Click here: \n${url}`;
    const reviewBody = `Hey,\nI recently left a review for ${company?.name}, you should check it out!\nHere is the link: ${url}`;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
    // iOS uses &, while Android expects ? before the message body.
    // Leave the recipient empty so the messaging app opens its recipient picker.
    const separator = isIOS ? '&' : '?';
    const body = encodeURIComponent(isOffer ? offerBody : reviewBody);
    window.location.assign(`sms:${separator}body=${body}`);
  };

  const copyUrlToClipboard = async () => {
    let didCopy = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        didCopy = true;
      } catch {
        // Fall back for browsers that expose Clipboard but deny access to it.
      }
    }

    if (!didCopy) {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';

      try {
        document.body.appendChild(textArea);
        textArea.select();
        didCopy = document.execCommand('copy');
      } catch {
        // The URL is displayed below the buttons if clipboard access fails.
      } finally {
        textArea.remove();
      }
    }

    setShareStatus(
      didCopy ? 'Link copied to clipboard!' : `Copy this link: ${url}`,
    );
  };

  const onClickSocialMedia = async () => {
    setShareStatus('');

    if (typeof navigator.share !== 'function') {
      await copyUrlToClipboard();
      return;
    }

    const shareMessage = isOffer
      ? `Click the link to claim this special offer${company?.name ? ` from ${company.name}` : ''}:`
      : `Check out my review of ${company?.name ?? 'SoTellUs'}:`;
    const shareLink = async () => {
      try {
        await navigator.share({
          text: shareMessage,
          url,
        });
        return true;
      } catch (error) {
        return error instanceof DOMException && error.name === 'AbortError';
      }
    };

    if (shareImageFile) {
      if (
        typeof navigator.canShare !== 'function'
        || !navigator.canShare({ files: [shareImageFile] })
      ) {
        if (!await shareLink()) {
          await copyUrlToClipboard();
        }
        return;
      }

      // Share the image as the primary item and provide the referral URL as
      // accompanying text so compatible targets receive both.
      try {
        await navigator.share({
          files: [shareImageFile],
          text: `${shareMessage} ${url}`,
        });
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          if (!await shareLink()) {
            await copyUrlToClipboard();
          }
        }
      }
      return;
    }

    if (!await shareLink()) {
      await copyUrlToClipboard();
    }
  };

  return (
    <div className="centered-stack">
      {referralOfferEnabled && company?.offerImgUrl &&
        <div className="logo-wrapper">
          <img className="logo-img" alt="logo-img" src={company?.offerImgUrl} />
        </div>
      }
      <Title
        className="header-text share-title"
        mb={10}
        mt={10}
        style={{ textWrap: 'balance' }}
      >
        {isOffer
          ? referralRewardsEnabled && !referralOfferEnabled
            ? `Refer ${companyName} to your family & friends`
            : `Share an offer from ${companyName} with your family & friends`
          : `Share your review of ${companyName} with your family & friends`}
        {showReward && (
          <>
            {' '}and earn{' '}
            <span style={{ color: 'brown' }}>${company?.promoterReward}!</span>
          </>
        )}
      </Title>
      {/* Facebook-labeled native share option */}
      <button
        className="platform-button"
        type="button"
        onClick={() => void onClickSocialMedia()}
      >
        <div className="social-icon">
          {facebookIconPrimary('30', '30')}
        </div>
        <p className="button-label">Share to Facebook</p>
      </button>

      {/* Instagram-labeled native share option */}
      <button
        className="platform-button"
        type="button"
        onClick={() => void onClickSocialMedia()}
      >
        <div className="social-icon">
          <IconBrandInstagram color="#E4405F" size={35} />
        </div>
        <p className="button-label">Share to Instagram</p>
      </button>

      {/* TikTok-labeled native share option */}
      <button
        className="platform-button"
        type="button"
        onClick={() => void onClickSocialMedia()}
      >
        <div className="social-icon">
          {tiktokIcon('30', '30')}
        </div>
        <p className="button-label">Share to TikTok</p>
      </button>

      {/* Native social media option */}
      <button
        className="platform-button"
        type="button"
        onClick={() => void onClickSocialMedia()}
      >
        <div className="social-icon">
          <IconShare color='gray' size={35} />
        </div>
        <p className="button-label">
          {isPreparingShareImage ? 'Preparing Social Media Image...' : 'Share to Social Media'}
        </p>
      </button>
      {shareStatus && <p role="status">{shareStatus}</p>}
      {/* Email Option */}
      <button className="platform-button" type="button" onClick={onClickEmail}>
        <div className="social-icon">
          <IconMail color='gray' size={35} />
        </div>
        <p className="button-label">Share through Email</p>
      </button>
      {/* Mobile Option */}
      {width < 750 && (
        <button
          className="platform-button"
          type="button"
          onClick={onClickMobile}
        >
          <div className="social-icon">
            <IconMessage2 color='gray' size={35} />
          </div>
          <p className="button-label">Share through SMS</p>
        </button>
      )}
    </div>
  );
}
